/**
 * @file AmbientFxService.ts
 * @role Persistent, continuously-drifting ambient decoration — distinct by design from
 *       ParticleService's burst-and-die bursts. Owns a TexturePool (Phase D) sized to the max
 *       `spriteCount` across all tiers (so tier changes never allocate mid-transition), a
 *       small set of "active" pooled Sprites that drift and wrap within the attached
 *       AmbientBackgroundLayer's bounds, and an internal `idle -> tierN -> idle` state machine
 *       driven by setTier()/returnToIdle(). Ticks on GameTicker's clamped dt (Invariant 22),
 *       never raw rAF time. Subscribes to three EventBus NOTIFICATIONS — 'spin:completed' calls
 *       returnToIdle(); 'win:bigWinStart' calls setTier(payload.tier); 'win:bigWinEnd' calls
 *       returnToIdle() — none of which carry control flow (AGENT_PROTOCOL's EventBus rule);
 *       WinPresentationController still owns win-presentation sequencing, this service only
 *       mirrors its tier for as long as the escalation window is open.
 * @owns Live ambient sprites, their drift state, the ambient TexturePool, and the current tier.
 * @reads ambient-fx.config.json (or injected raw config); a TextureResolver for the existing
 *        fruit-symbol textures it draws from; an AmbientBackgroundLayer via attach().
 * @neverDoes Read raw rAF/Date for motion (uses injected dt). Decide outcomes. Classify win
 *            tiers from spin:completed's payload (that would duplicate WinDtoMapper —
 *            WinPresentationController does that and tells this service the result via
 *            'win:bigWinStart'/'win:bigWinEnd'). Destroy/respawn sprites on drift (wraps
 *            instead — persistent, not bursty).
 * @invariants 10 (config validated), 22 (dt-driven). The tier events it subscribes to are
 *             notifications, never control flow — see AGENT_PROTOCOL's EventBus rule.
 * @see src/core/particles/TexturePool.ts, src/core/particles/ParticleService.ts,
 *      src/game/ambient/AmbientBackgroundLayer.ts, src/core/events/EventBus.ts
 */
import { Texture, type Sprite } from 'pixi.js';
import { z } from 'zod';
import ambientFxConfigRaw from '@config/ambient-fx.config.json';
import { GameError } from '@core/errors/GameError';
import { TexturePool } from '@core/particles/TexturePool';
import type { TextureResolver } from '@core/assets/TextureResolver';
import type { EventBus } from '@core/events/EventBus';
import type { AmbientBackgroundLayer } from './AmbientBackgroundLayer';

const AmbientTierConfigSchema = z
  .object({
    spriteCount: z.number().int().nonnegative(),
    driftSpeedMin: z.number().nonnegative(),
    driftSpeedMax: z.number().nonnegative(),
  })
  .refine((t) => t.driftSpeedMax >= t.driftSpeedMin, {
    message: 'driftSpeedMax must be >= driftSpeedMin',
  });

export const AmbientFxConfigSchema = z
  .object({
    textureIds: z.array(z.string().min(1)).min(1),
    /** Rendered sprite size range (px, the longer texture edge scaled to fit) — the reused
     *  fruit-symbol textures are authored for full reel-cell art, so ambient decoration must be
     *  scaled down explicitly rather than rendered at native texture resolution. */
    spriteSizeMinPx: z.number().positive(),
    spriteSizeMaxPx: z.number().positive(),
    tiers: z.object({
      idle: AmbientTierConfigSchema,
      small: AmbientTierConfigSchema,
      medium: AmbientTierConfigSchema,
      big: AmbientTierConfigSchema,
      jackpot: AmbientTierConfigSchema,
    }),
  })
  .refine((c) => c.spriteSizeMaxPx >= c.spriteSizeMinPx, {
    message: 'spriteSizeMaxPx must be >= spriteSizeMinPx',
  });

export type AmbientFxConfig = z.infer<typeof AmbientFxConfigSchema>;
export type AmbientTierConfig = z.infer<typeof AmbientTierConfigSchema>;
export type AmbientTier = keyof AmbientFxConfig['tiers'];

export function validateAmbientFxConfig(raw: unknown): AmbientFxConfig {
  const parsed = AmbientFxConfigSchema.safeParse(raw);
  if (!parsed.success) {
    throw new GameError(
      'CONFIG_LOAD_FAILED',
      `Invalid ambient-fx config: ${parsed.error.message}`,
      { cause: parsed.error },
    );
  }
  return parsed.data;
}

interface ActiveSprite {
  sprite: Sprite;
  x: number;
  y: number;
  vx: number;
  vy: number;
}

const TIER_ORDER: readonly AmbientTier[] = ['idle', 'small', 'medium', 'big', 'jackpot'];

/** Wraps `value` into `[min, min + range)`, handling any magnitude of overshoot in one step
 *  (modulo, not a single subtract) so a large clamped dt never leaves a sprite outside range.
 *  Used with a viewport-plus-margin range so sprites drift fully off-screen (past the visible
 *  edge by `margin`) before reappearing on the opposite side, instead of teleporting the instant
 *  they touch the visible boundary. */
function wrapRange(value: number, min: number, range: number): number {
  if (range <= 0) return min;
  return ((((value - min) % range) + range) % range) + min;
}

export class AmbientFxService {
  private readonly config: AmbientFxConfig;
  private readonly resolveTexture: TextureResolver;
  private readonly random: () => number;
  private readonly unsubscribeSpin: (() => void) | undefined;
  private readonly unsubscribeBigWinStart: (() => void) | undefined;
  private readonly unsubscribeBigWinEnd: (() => void) | undefined;

  private layer: AmbientBackgroundLayer | undefined;
  private pool: TexturePool | undefined;
  private readonly active: ActiveSprite[] = [];
  private tier: AmbientTier = 'idle';
  /** True once the attached layer has reported real (non-zero) viewport bounds at least once.
   *  attach() commonly runs before the scene's first layout pass, when the layer's bounds are
   *  still 0x0 — spawn positions computed at that point would collapse into the origin corner.
   *  We defer trusting spawn positions until the first real resize() notification arrives. */
  private hasRealBounds = false;
  private densityMultiplier = 1;
  private unsubscribeResize: (() => void) | undefined;

  constructor(
    raw: unknown = ambientFxConfigRaw,
    resolveTexture: TextureResolver = () => undefined,
    eventBus?: EventBus,
    random: () => number = Math.random,
  ) {
    this.config = validateAmbientFxConfig(raw);
    this.resolveTexture = resolveTexture;
    this.random = random;
    this.unsubscribeSpin = eventBus?.on('spin:completed', () => this.returnToIdle());
    // Win-tier density moved to PresentationSequenceCoordinator (issue #105, ADR 0010), which
    // times it against the rest of the score. 'spin:completed' above stays: returning to idle
    // when a round ends is this service's own lifecycle, not choreography.
  }

  /** Attaches to a render layer, allocating the pool (once) and populating the idle tier.
   *  If the layer doesn't yet know its real viewport size (e.g. attach() runs in the scene
   *  constructor, before the first layout pass), spawn positions are provisional and get
   *  re-seeded once the layer reports real bounds via its onResize() notification. */
  attach(layer: AmbientBackgroundLayer): void {
    this.unsubscribeResize?.();
    this.layer = layer;
    if (!this.pool) {
      this.pool = new TexturePool(Texture.WHITE, this.computeMaxPoolSize());
    }
    this.hasRealBounds = layer.viewportWidth > 0 && layer.viewportHeight > 0;
    this.unsubscribeResize = layer.onResize((width, height) => {
      if (this.hasRealBounds || width <= 0 || height <= 0) return;
      this.hasRealBounds = true;
      this.reseedActivePositions();
    });
    this.applyTier(this.tier);
  }

  /** Re-rolls every currently-active sprite's position across the layer's now-known real
   *  bounds. Called once, the first time attach() is followed by a resize() that provides
   *  non-zero dimensions — corrects the corner-clustered fallback spawn from attach(). Spawns
   *  just outside the visible edge (same as a fresh spawn in applyTier()) rather than directly
   *  inside the viewport, so sprites drift into view instead of popping in on layout. */
  private reseedActivePositions(): void {
    if (!this.layer) return;
    const width = this.layer.viewportWidth;
    const height = this.layer.viewportHeight;
    if (width <= 0 || height <= 0) return;
    for (const entry of this.active) {
      const { x, y } = this.spawnEdgePosition(width, height);
      entry.x = x;
      entry.y = y;
      entry.sprite.position.set(entry.x, entry.y);
    }
  }

  /** Approximate off-screen buffer, in px, a sprite must clear before it's considered fully
   *  hidden — derived from the configured max render size so bigger sprites get a bigger
   *  buffer and never visibly clip at the edge when they wrap. */
  private get edgeMarginPx(): number {
    return this.config.spriteSizeMaxPx;
  }

  /** Picks a random point just outside one of the four viewport edges (by `edgeMarginPx`), so a
   *  freshly (re)spawned sprite starts fully off-screen and drifts into view rather than
   *  appearing suddenly inside the visible area. */
  private spawnEdgePosition(width: number, height: number): { x: number; y: number } {
    const margin = this.edgeMarginPx;
    const edge = Math.floor(this.random() * 4);
    const along = (span: number): number => this.random() * span;
    const beyond = (): number => margin + this.random() * margin;
    switch (edge) {
      case 0: // left
        return { x: -beyond(), y: along(height) };
      case 1: // right
        return { x: width + beyond(), y: along(height) };
      case 2: // top
        return { x: along(width), y: -beyond() };
      default: // bottom
        return { x: along(width), y: height + beyond() };
    }
  }

  /** Scales a sprite from its native (full reel-symbol-art) texture resolution down to a small
   *  ambient-decoration size, picked within the configured min/max render size. */
  private scaleSprite(sprite: Sprite): void {
    const size =
      this.config.spriteSizeMinPx +
      this.random() * (this.config.spriteSizeMaxPx - this.config.spriteSizeMinPx);
    const textureSize = Math.max(sprite.texture.width, sprite.texture.height, 1);
    sprite.scale.set(size / textureSize);
  }

  /** Pool is sized once from every tier's spriteCount, known at construction — no mid-transition allocation. */
  private computeMaxPoolSize(): number {
    return Math.max(
      0,
      ...TIER_ORDER.map((t) =>
        Math.round(this.config.tiers[t].spriteCount * this.densityMultiplier),
      ),
    );
  }

  setDensityMultiplier(multiplier: number): void {
    this.densityMultiplier = Math.max(0, Math.min(1, multiplier));
    if (this.pool) {
      this.clear();
      this.pool.destroy();
      this.pool = new TexturePool(Texture.WHITE, this.computeMaxPoolSize());
    }
    this.applyTier(this.tier);
  }

  /** Escalate (or change) the ambient tier. A no-op shape-wise if already at `tier`. */
  setTier(tier: AmbientTier): void {
    this.tier = tier;
    this.applyTier(tier);
  }

  /** Return to the idle tier. Called on every 'spin:completed' notification. */
  returnToIdle(): void {
    this.setTier('idle');
  }

  get currentTier(): AmbientTier {
    return this.tier;
  }

  get activeCount(): number {
    return this.active.length;
  }

  /** Test/diagnostic hook: pool allocation stats, to assert no growth across tier changes. */
  get poolStats(): { createdCount: number; freeCount: number } {
    return { createdCount: this.pool?.createdCount ?? 0, freeCount: this.pool?.freeCount ?? 0 };
  }

  private applyTier(tier: AmbientTier): void {
    if (!this.layer || !this.pool) return;
    const def = this.config.tiers[tier];

    const targetCount = Math.round(def.spriteCount * this.densityMultiplier);
    while (this.active.length > targetCount) {
      const removed = this.active.pop();
      if (removed) this.pool.release(removed.sprite);
    }
    while (this.active.length < targetCount) {
      const sprite = this.pool.acquire();
      sprite.texture = this.pickTexture();
      this.scaleSprite(sprite);
      const width = this.layer.viewportWidth || 1;
      const height = this.layer.viewportHeight || 1;
      const { x, y } = this.spawnEdgePosition(width, height);
      sprite.position.set(x, y);
      this.layer.addChild(sprite);
      this.active.push({ sprite, x, y, vx: 0, vy: 0 });
    }
    // Re-roll every active sprite's drift velocity to the new tier's speed range so escalation
    // (and de-escalation back to idle) is felt immediately, not just via sprite count.
    for (const entry of this.active) {
      const speed = def.driftSpeedMin + this.random() * (def.driftSpeedMax - def.driftSpeedMin);
      const horizontalBias = (this.random() - 0.5) * 0.6; // gentle sideways float, mostly downward
      entry.vy = speed;
      entry.vx = speed * horizontalBias;
    }
  }

  private pickTexture(): Texture {
    const ids = this.config.textureIds;
    const id = ids[Math.floor(this.random() * ids.length)] ?? ids[0];
    const resolved = id ? this.resolveTexture(id) : undefined;
    return resolved ?? Texture.WHITE;
  }

  /** Advances drift on the CLAMPED GameTicker dt (Invariant 22) and wraps at the PADDED layer
   *  bounds (viewport + edgeMarginPx on each side) — a sprite must drift fully past the visible
   *  edge before it wraps to the opposite side, so it always exits and re-enters off-screen
   *  instead of teleporting the instant it touches the visible boundary. */
  update(dtMs: number): void {
    if (!this.layer || this.active.length === 0) return;
    const width = this.layer.viewportWidth;
    const height = this.layer.viewportHeight;
    if (width <= 0 || height <= 0) return;
    const margin = this.edgeMarginPx;
    const dt = dtMs / 1000;
    for (const entry of this.active) {
      entry.x = wrapRange(entry.x + entry.vx * dt, -margin, width + margin * 2);
      entry.y = wrapRange(entry.y + entry.vy * dt, -margin, height + margin * 2);
      entry.sprite.position.set(entry.x, entry.y);
    }
  }

  /** Releases every active sprite back to the pool without destroying the pool itself. */
  clear(): void {
    for (const entry of this.active) this.pool?.release(entry.sprite);
    this.active.length = 0;
  }

  /** Full teardown: releases sprites, destroys the pool, detaches the layer, unsubscribes. */
  dispose(): void {
    this.clear();
    this.pool?.destroy();
    this.pool = undefined;
    this.unsubscribeResize?.();
    this.unsubscribeResize = undefined;
    this.layer = undefined;
    this.unsubscribeSpin?.();
    this.unsubscribeBigWinStart?.();
    this.unsubscribeBigWinEnd?.();
  }
}
